import axios from 'axios';
import { logError } from 'loggingManager';
import { Shape } from 'components/smartShapes/shapeData/types.shapes';
import { URLs, DiscoveryShapesDefaults as Defaults } from 'utility/constants';

interface DiscoveryFacetItem {
    key: string;
    count: number;
}

interface DiscoveryAsset {
    id: string;
    deliveryResources: { original: string };
}

interface DiscoveryListResponse<T> {
    _embedded: { item: T[] };
    offset: string | null;
}

export interface ShapeGroup {
    collectionName: string;
    items: Shape[];
}

const getBaseUrlAndParams = (tags?: string[]): { url: string; query: URLSearchParams } => {
    const params = new URLSearchParams();
    const allTags = tags && tags.length > 0 ? tags : [Defaults.tag];
    const tenantPart = `${Defaults.accountId}/${Defaults.contentAreaId}`;

    params.set('AssetKind', 'clipart');
    params.append('Collections.Values', Defaults.collection);
    params.set('Collections.Match', 'all');
    params.set('CustomProperties[shapesApiAppId]', Defaults.appId);
    allTags.forEach((tag) => params.append('Tags.Values', tag));
    params.set('Tags.Match', 'all');
    params.set('limit', String(Defaults.pageSize));
    params.set('requestor', 'Template-Maker');

    return { url: `${URLs.DiscoveryBaseUrl}/api/v1/${tenantPart}`, query: params };
};

const getCollections = async (tags?: string[]): Promise<string[]> => {
    const baseUrlAndParams = getBaseUrlAndParams(tags);
    baseUrlAndParams.query.set('Facet', 'collections');
    baseUrlAndParams.query.set('status', 'enabled');

    const { data } = await axios.get<DiscoveryListResponse<DiscoveryFacetItem>>(
        `${baseUrlAndParams.url}/assets/facets:paginate?${baseUrlAndParams.query}`,
    );

    return (data?._embedded?.item ?? [])
        .map((facet) => facet.key)
        .filter((key) => key !== Defaults.collection);
};

const getAssets = async (collection: string, tags?: string[]): Promise<Shape[]> => {
    const baseUrlAndParams = getBaseUrlAndParams(tags);
    baseUrlAndParams.query.append('Collections.Values', collection);

    const { data } = await axios.get<DiscoveryListResponse<DiscoveryAsset>>(
        `${baseUrlAndParams.url}/assets?${baseUrlAndParams.query}`,
    );

    return data?._embedded?.item && Array.isArray(data?._embedded?.item)
        ? data?._embedded?.item
            .filter((asset) => asset.deliveryResources.original)
            .map((asset) => ({
                id: asset.id,
                url: asset.deliveryResources.original,
            }))
        : [];
};

export const getShapesAssetList = async (tags?: string[]): Promise<ShapeGroup[]> => {
    try {
        const collections = await getCollections(tags);

        const shapeGroups = await Promise.all(collections.map(async (name) => ({
            collectionName: name,
            items: await getAssets(name, tags),
        })));

        return shapeGroups.filter((collection) => collection.items.length > 0);
    } catch (err) {
        logError(`Failed to fetch shapes from Discovery API: ${err}`);
        return [];
    }
};
